import type { Currency } from '../money/money';

/**
 * What a report figure can measure (gitaumoses4/chukua#200). The per-report tickets (#201-209) choose among
 * these rather than inventing a shape, so the web and the .xlsx export render any report the same way.
 */
export const METRIC_KINDS = ['count', 'money', 'ratio', 'duration_seconds'] as const;
export type MetricKind = (typeof METRIC_KINDS)[number];

/** The filters every report may take, in the order the filter bar shows them. A report declares which it honours. */
export const REPORT_FILTER_KEYS = ['from', 'to', 'asOf', 'customerId', 'truckId', 'driverId', 'originZone', 'destinationZone', 'currency'] as const;
export type ReportFilterKey = (typeof REPORT_FILTER_KEYS)[number];

/**
 * A measured value. Money is integer minor units with its currency and is never summed across currencies, so a
 * report with two currencies has two figures. A ratio keeps its numerator and denominator as integers (a float
 * would lose the drill-through: the records behind it are the denominator's).
 */
export type MetricValue =
  | { kind: 'count'; value: number }
  | { kind: 'money'; amountMinor: number; currency: Currency }
  | { kind: 'ratio'; numerator: number; denominator: number }
  | { kind: 'duration_seconds'; value: number };

/** Where a figure's records are: the report and the filters that narrow it to exactly those records. The web builds the link. */
export interface DrillThrough {
  reportKey: string;
  filters: Partial<Record<ReportFilterKey, string | string[]>>;
}

export interface ReportFigure {
  /** What the figure is about, e.g. a customer name; `group` carries the keys that identify it. */
  label: string;
  group: Record<string, string>;
  metric: MetricValue;
  drillThrough: DrillThrough;
}

/** One column of a report's records list, so the screen and the export need no per-report code. */
export interface ReportColumn {
  key: string;
  label: string;
  kind: 'text' | 'date' | 'money' | 'number';
}

export interface ReportSummary {
  key: string;
  title: string;
  description: string;
  /** The filters this report honours; the filter bar disables the rest. */
  filters: ReportFilterKey[];
  metricKind: MetricKind;
  /**
   * The columns of the records list. A `money` column's currency is the record's `currency` value, so every report with a
   * money column must also return `currency`; the export writes it as its own column.
   */
  columns: ReportColumn[];
  /** `view`: served from a materialised view, so figures may be older than the clock (see `refreshedAt`). `live`: read from the tables on every request. */
  source: 'live' | 'view';
  /** When the view behind a `view` report was last refreshed; null for `live`, or a view not yet refreshed. */
  refreshedAt: string | null;
  /** Set when the last scheduled refresh of the view failed, so the screen can say the figures are older than they should be. */
  refreshFailedAt: string | null;
  /** Whether `GET /reports/:key/trend` answers. */
  hasTrend: boolean;
}

/** One point of a trend: a period (`YYYY-MM`) and the report's metric over it. */
export interface ReportTrendPoint {
  period: string;
  metric: MetricValue;
}

/** A trend is one series per currency, because money is never summed across currencies; a non-money trend has one series with `currency: null`. */
export interface ReportTrendSeries {
  currency: Currency | null;
  points: ReportTrendPoint[];
}

export interface ReportTrend {
  series: ReportTrendSeries[];
}

/** A choice for a filter's picker: the value the filter takes and the words the user sees. */
export interface ReportFilterOption {
  value: string;
  label: string;
}

/** The filters whose values are records the user picks from (`currency` is a fixed list the web already has). */
export const REPORT_OPTION_FILTER_KEYS = ['customerId', 'truckId', 'driverId', 'originZone', 'destinationZone', 'currency'] as const;
export type ReportOptionFilterKey = (typeof REPORT_OPTION_FILTER_KEYS)[number];

/** Most options one lookup returns; typing narrows it. */
export const REPORT_OPTIONS_LIMIT = 25;
